import { Link } from 'react-router-dom';
import GuideLayout from '../../components/guides/GuideLayout';
import GuideCTA from '../../components/guides/GuideCTA';
import { GuideSection, GuideFaqItem, GuideChecklist, GuideNote, SourceLink } from '../../components/guides/GuideParts';

const SLUG = 'how-to-request-your-school-records';

export const RequestSchoolRecords = () => (
  <GuideLayout slug={SLUG}>
    <GuideSection>
      <p>
        Schools hold more about you than reports and attendance registers. For anyone who was in
        care, moved placements, or had extra support needs, a school file can include detailed
        notes from teachers, pastoral staff and, if you had one, an Education, Health and Care
        Plan (EHCP) or its predecessor, a Statement of Special Educational Needs. It is worth
        requesting from every school you attended, not just the last one.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Who to ask" title="Which school, academy trust, or council?">
      <GuideChecklist
        items={[
          "A maintained (council-run) school usually holds its own records, though the local authority's education department may hold overlapping SEN records.",
          'An academy holds its own records independently of the council — the academy trust is the data controller, not the local authority.',
          'If a school has since closed, merged, or converted to an academy, its historic records are usually transferred either to the local authority or to whichever school absorbed its pupils — the current school, or the council, can usually tell you where they went.',
          "If you had an EHCP or Statement, the local authority's SEN team holds its own file alongside whatever the school itself holds — it's worth asking both.",
        ]}
      />
    </GuideSection>

    <GuideSection eyebrow="Two different rights" title="'Educational record' regulations vs a full SAR">
      <p>
        Maintained schools have a second, older access right that sits alongside a SAR, and it is
        easy to end up asking for the wrong one, so it is worth knowing how they differ.
      </p>
      <GuideChecklist
        items={[
          <>
            Under{' '}
            <SourceLink href="https://www.legislation.gov.uk/uksi/2005/1437/regulation/5">
              The Education (Pupil Information) (England) Regulations 2005
            </SourceLink>
            , a maintained school or non-maintained special school must give a parent, or an
            adult former pupil, access to their <strong className="text-ink">educational
            record</strong> within 15 school days — much faster than the one-month SAR deadline,
            and free to inspect. It specifically includes any EHC plan or personal education
            plan.
          </>,
          'But it is a narrower right, not a bigger one: the regulations themselves say a school cannot disclose anything this way that it could not lawfully disclose under the UK GDPR anyway. It is a faster route to the same underlying right, capped by the same rules.',
          "Academies are not covered by these regulations at all — they only reach schools maintained by a local authority and non-maintained special schools. If you went to an academy, the ordinary one-month UK GDPR SAR is your only formal route, though some academies choose to mirror the faster standard anyway.",
        ]}
      />
      <p>
        In practice, a full Subject Access Request under Article 15 UK GDPR is the safer, broader
        thing to ask for either way — it reaches everything the school holds about you, not just
        whatever narrowly counts as the formal educational record, works identically whether it
        is a maintained school or an academy, and is what the rest of this guide is written
        around.
      </p>
    </GuideSection>

    <GuideSection eyebrow="What to ask for" title="What a school record usually contains">
      <GuideChecklist
        items={[
          'Your full pupil file, including reports, assessments and any pastoral or safeguarding notes.',
          'SEN records, including any EHCP, Statement, or earlier School Action / School Action Plus paperwork.',
          'Behaviour records, including any exclusions — fixed-term or permanent — and the reasons given.',
          'Records of meetings the school held about you, such as review meetings for looked-after children (often called PEP — Personal Education Plan — meetings).',
          'Correspondence between the school and your social worker, virtual school, or foster carer.',
        ]}
      />
      <p>
        If you were a looked-after child, your virtual school — the local authority team
        responsible for the educational outcomes of children in its care — may also hold PEP
        records worth requesting separately, especially if you moved between several schools.
      </p>
    </GuideSection>

    <GuideNote>
      <p>
        <strong>Grades and exam results are the easy part.</strong> If all you want is proof of
        results, exam boards and awarding organisations can usually reissue certificates more
        quickly than a full SAR. A SAR is worth the effort when you want the fuller record — why
        decisions were made, what staff observed, how support was (or wasn&apos;t) put in place.
      </p>
    </GuideNote>

    <GuideCTA />

    <GuideSection eyebrow="What happens next" title="Deadlines and what to expect">
      <p>
        A school, academy trust or local authority education team has{' '}
        <strong className="text-ink">one calendar month</strong> to respond, extendable by up to
        two further months for a genuinely complex request — several years&apos; worth of pastoral
        and SEN records across more than one school can reasonably count. Some material may be
        redacted where it identifies another pupil, but anything a teacher or member of staff
        wrote about you should not be.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Common questions" title="Questions people ask us most" tinted>
      <div className="space-y-2" data-testid="school-records-faq">
        <GuideFaqItem
          q="I went to several schools growing up. Do I need to ask each one separately?"
          a="Yes — each school is a separate data controller (or the council is, for records it holds centrally), so a request to one won't produce records held by another. List every school you remember, even approximately, and request from each."
        />
        <GuideFaqItem
          q="What if the school has closed down?"
          a="Its records don't disappear. Ask the local authority in the area the school was in — they usually hold, or know who holds, records from closed maintained schools. For a closed academy, the trust that ran it, or its successor trust, is the place to start."
        />
        <GuideFaqItem
          q="Can I see what my teachers actually wrote about me, not just the official report?"
          a="Generally yes — internal notes, assessments and pastoral records about you are your personal data too, not just the polished version sent home. A school can't withhold internal material simply because it wasn't meant for you to see at the time."
        />
        <GuideFaqItem
          q="Were exclusion decisions recorded, and can I see the reasoning?"
          a="Yes. Records of any fixed-term or permanent exclusion, and the reasons given for it, should be part of your file and included in a full SAR response."
        />
        <GuideFaqItem
          q="Should I ask for my 'educational record' or make a Subject Access Request?"
          a="Ask for a Subject Access Request. The educational record right is faster for a maintained school, but it's capped by the same UK GDPR rules and doesn't apply to academies at all — a SAR reaches everything a school holds about you, whichever kind of school it was, so it's the one route that always works."
        />
      </div>
    </GuideSection>

    <GuideSection eyebrow="Read more" title="Where this comes from">
      <p>
        The ICO has specific guidance for{' '}
        <SourceLink href="https://ico.org.uk/for-the-public/schools/">
          schools, universities and colleges
        </SourceLink>
        , alongside its general{' '}
        <SourceLink href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/individual-rights/right-of-access/">
          right of access guidance
        </SourceLink>
        . For the basics of what a SAR is, see our own{' '}
        <Link to="/guides/what-is-a-subject-access-request" className="text-brand underline hover:opacity-90">
          plain-English guide
        </Link>
        .
      </p>
    </GuideSection>

    <GuideCTA
      heading="List every school — we'll write to all of them"
      body="Add each school you remember, even roughly, and we'll draft the requests, send them, and track every deadline so nothing gets lost between schools."
      testIdPrefix="guide-cta-end"
    />
  </GuideLayout>
);

export default RequestSchoolRecords;
